import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import {
  ButtonComponent,
  IconComponent,
  modalFn,
  SoftSlotDirective,
} from '@softline/ui-core';
import { GarageSignatureModalComponent } from '../../modals/signature-modal/signature-modal.component';

@Component({
  selector: 'soft-garage-signature-field',
  templateUrl: './signature-field.component.html',
  styleUrl: './signature-field.component.css',
  imports: [ButtonComponent, IconComponent, SoftSlotDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GarageSignatureFieldComponent {
  private readonly modal = modalFn();
  private readonly sanitizer = inject(DomSanitizer);

  // Accepts a Blob (freshly captured signature) or a string URL / data URL
  // (stored signature loaded for display).
  readonly value = input<Blob | string | null>(null);
  readonly readonly = input(false);
  readonly loading = input(false);
  readonly signed = output<Blob>();

  protected readonly imageUrl = signal<SafeUrl | null>(null);
  protected readonly modalOpen = signal(false);

  constructor() {
    effect((onCleanup) => {
      const value = this.value();
      if (!value) {
        this.imageUrl.set(null);
        return;
      }
      if (typeof value === 'string') {
        this.imageUrl.set(this.sanitizer.bypassSecurityTrustUrl(value));
        return;
      }
      const url = URL.createObjectURL(value);
      this.imageUrl.set(this.sanitizer.bypassSecurityTrustUrl(url));
      onCleanup(() => URL.revokeObjectURL(url));
    });
  }

  protected async openSignatureModal(): Promise<void> {
    if (this.readonly() || this.modalOpen()) return;
    this.modalOpen.set(true);
    try {
      const result = await this.modal({
        component: GarageSignatureModalComponent,
        title: 'Unterschreiben',
        dismiss: true,
        subtitle: 'Bitte unterschreiben Sie hier, um den Auftrag abzuschließen.',
        icon: 'fa-regular fa-pen-to-square',
      });
      if (!result || result === 'DISMISSED') return;
      this.signed.emit(result);
    } finally {
      this.modalOpen.set(false);
    }
  }
}
